/**
 * Company logos for a person's Experience rows: the logo ChopLink captured for
 * each job's LinkedIn company, instead of initials. One request per person
 * (lookup and signed URLs together); a job whose company has no stored logo,
 * or a failed request, keeps its initials.
 */
import { skipToken, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import type { PeopleClient } from "../../clients/people.client";

/** Signed logo URLs last an hour; refresh well before that. */
const LOGO_URLS_STALE_MS = 30 * 60 * 1000;

export function useJobLogos(
  client: PeopleClient,
  linkedinIds: ReadonlyArray<string | null | undefined>
): ReadonlyMap<string, string> {
  // Keyed on the ids' content: the caller builds a new array every render.
  const idsKey = Array.from(new Set(linkedinIds.filter((i): i is string => Boolean(i))))
    .sort()
    .join(",");
  const ids = useMemo(() => (idsKey ? idsKey.split(",") : []), [idsKey]);
  const query = useQuery({
    queryKey: ["person-panel", "job-logos", ids],
    queryFn: ids.length > 0 ? () => client.jobLogos(ids) : skipToken,
    staleTime: LOGO_URLS_STALE_MS,
    retry: false,
  });
  return useMemo(() => new Map(Object.entries(query.data ?? {})), [query.data]);
}
